/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/strobe/strobe.d.ts" />

namespace Application.Directives {
    'use strict';

    /**
     * Factory callback type
     */
    declare type VideoPlayerDirectiveLegacyCb = ($timeout: angular.ITimeoutService,
                                           $interval: angular.IIntervalService,
                                           connectionRoomService: Services.ConnectionRoomService,
                                           modalService: ngModal.IModalService,
                                           messageTranslationsService: Services.MessageTranslationsService ) => VideoPlayerDirectiveLegacy;

    /**
     * Custom scope
     */
    export interface IVideoPlayerLegacyScopeDef {
        streamUrl: string;
        streamType: string;
        autoplay: string;
        loop: string;
        live: string;
        videoScaling: string;
        showThumb: string;
        streamPipeId: string;
        onVideoPlayAttempt: string;
        onVideoPlayError: string;
    }

    /**
     * Custom scope
     */
    export interface IVideoPlayerLegacyScope extends ng.IScope {
        THUMBNAIL_FAIL_IMG: string;
        streamUrl: string;
        streamType: string;
        autoplay: boolean;
        loop: boolean;
        live: boolean;
        videoScaling: string;
        showThumb: boolean;
        thumbEnabled: boolean;
        type: string;
        playerId: number;
        clickThumb: () => void;
        reloadPlayer: () => void;
        backToThumbMode: () => void;
        strobePlayer: StrobePlayer;
        initStrobe: () => void;
        reloadStrobe: () => void;
        thumbnail: string;
        streamPipeId: string;
        onVideoPlayAttempt: () => void;
        onVideoPlayError: () => void;
        loadVideoFeed: (initial?: boolean) => void;
        loadThumbnails: () => void;
        videofeed: Models.IVideoFeed;
        thumbnailTimer: angular.IPromise<void>;
        videofeedTimer: angular.IPromise<void>;
    }

    export class VideoPlayerDirectiveLegacy implements ng.IDirective {
        public static $name: string = "videoPlayerLegacy";
        public static $inject: string[] = [];

        private static THUMBNAIL_LOADING_IMG: string = "images/thumbnail-loading.png";
        private static THUMBNAIL_FAIL_IMG: string = "images/thumbnail-fail.png";

        public link: (scope: IVideoPlayerLegacyScope, element: JQuery, attrs: ng.IAttributes) => void;
        public restrict: string = 'AE';
        public transclude: boolean = false;
        public templateUrl: string = 'decorators/video-player-directive-legacy.html';
        public scope: IVideoPlayerLegacyScopeDef = {
            streamUrl: '@',
            streamType: '@?',
            autoplay: '=?',
            loop: '=?',
            live: '=?',
            videoScaling: '@?',
            showThumb: '=?',
            streamPipeId: '@?',
            onVideoPlayAttempt: '&',
            onVideoPlayError: '&',
        };

        private THUMBNAIL_TIMER_MS: number = 4000;
        private VIDEOFEED_TIMER_MS: number = 5000;

        /**
         * Factory for the directive
         */
        public static Factory(): VideoPlayerDirectiveLegacyCb {
            let directive: VideoPlayerDirectiveLegacyCb = ($timeout: angular.ITimeoutService,
                                                     $interval: angular.IIntervalService,
                                                     connectionRoomService: Services.ConnectionRoomService,
                                                     modalService: ngModal.IModalService,
                                                     messageTranslationsService: Services.MessageTranslationsService ) => {
                return new VideoPlayerDirectiveLegacy($timeout, $interval, connectionRoomService, modalService, messageTranslationsService);
            };
            directive.$inject = ['$timeout', '$interval', 'connectionRoomService', 'ModalService', 'messageTranslationsService'];
            return directive;
        }

        /**
         * Constructor
         */
        constructor($timeout: angular.ITimeoutService,
                    $interval: angular.IIntervalService,
                    connectionRoomService: Services.ConnectionRoomService,
                    modalService: ngModal.IModalService,
                    messageTranslationsService: Services.MessageTranslationsService ) {

            VideoPlayerDirectiveLegacy.prototype.link = (scope: IVideoPlayerLegacyScope, element: JQuery, attrs: ng.IAttributes) => {
                this.init(scope);

                scope.initStrobe = (): void => {
                    // recreates the DOM for the strobe player to be created again
                    let parent: JQuery = $('#player_' + scope.playerId).parent();
                    $('#player_' + scope.playerId).remove();
                    scope.playerId = Common.Utils.randomNumber(100000000);
                    parent.append(`<div class="video-player-legacy" id="player_${scope.playerId}">
                                    <div id="inner_player_${scope.playerId}" />
                                </div>`);

                    scope.strobePlayer = new StrobeMediaPlayback('inner_player_' + scope.playerId,
                        scope.streamUrl,
                        scope.autoplay,
                        scope.live);
                    if (scope.showThumb) {
                        scope.strobePlayer.registerOnRemoved(scope.backToThumbMode);
                    } else {
                        scope.strobePlayer.registerOnRemoved(scope.reloadPlayer);
                    }

                    scope.strobePlayer.init();
                };

                scope.reloadPlayer = (): void => {
                    if (scope.onVideoPlayError !== undefined) {
                        scope.onVideoPlayError();
                    }

                    $timeout(() => {
                        if (scope.strobePlayer) {
                            scope.strobePlayer.destroy();
                        }

                        scope.initStrobe();
                    });
                };

                scope.backToThumbMode = (): void => {
                    if (scope.onVideoPlayError !== undefined) {
                        scope.onVideoPlayError();
                    }

                    scope.strobePlayer.destroy();
                    $('#player_' + scope.playerId).remove();
                    scope.thumbEnabled = true;
                    this.initVideoFeed(scope, $interval);
                    messageTranslationsService.alertOnError('There are problems with the video stream. Playback stopped.', 'bottom-center');
                };

                scope.clickThumb = (): void => {
                    modalService.showModal({
                        templateUrl: Application.Controllers.ConfirmModalController.templateURL,
                        controller: Application.Controllers.ConfirmModalController.$name,
                        inputs: {
                            title: 'Stream play',
                            description: "Playing the stream could affect the ongoing transmission. Do you want to continue?",
                            name: '',
                            type: 'warning',
                        },
                    }).then((modal: ngModal.IModalObject) => {
                        let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.ConfirmModalController.ID,
                            {bgclose: false, modal: false, keyboard: false});
                        modalElem.show();
                        modal.close.then((result: Object) => {
                            modalElem.hide();
                            if (result) {
                                if (scope.thumbnailTimer)
                                    $interval.cancel(scope.thumbnailTimer);
                                if (scope.videofeedTimer)
                                    $interval.cancel(scope.videofeedTimer);
                                scope.thumbEnabled = false;

                                if (scope.onVideoPlayAttempt !== undefined) {
                                    scope.onVideoPlayAttempt();
                                }

                                $timeout(() => {
                                    scope.initStrobe();
                                });
                            }
                        });
                    });
                };

                scope.$on('$destroy', () => {
                    if (scope.strobePlayer)
                        scope.strobePlayer.destroy();

                    if (scope.thumbnailTimer)
                        $interval.cancel(scope.thumbnailTimer);

                    if (scope.videofeedTimer)
                        $interval.cancel(scope.videofeedTimer);
                });

                scope.THUMBNAIL_FAIL_IMG = VideoPlayerDirectiveLegacy.THUMBNAIL_FAIL_IMG;
                scope.thumbnail = VideoPlayerDirectiveLegacy.THUMBNAIL_LOADING_IMG;

                scope.loadThumbnails = () => {
                    // reloads the thumbnail, if available, with a new timestamp
                    if (scope.videofeed && scope.videofeed.status !== Models.VideoFeedStatus.ERROR) {
                        if (scope.videofeed.thumbnailUrl !== "") {
                            if (scope.videofeed.forceRefreshThumbnail)
                                scope.thumbnail = scope.videofeed.thumbnailUrl + "?&" + Common.Utils.getCurrentTimestamp();
                            else
                                scope.thumbnail = scope.videofeed.thumbnailUrl;
                        } else {
                            scope.thumbnail = VideoPlayerDirectiveLegacy.THUMBNAIL_FAIL_IMG;
                        }
                    }
                };

                scope.loadVideoFeed = (initial?: boolean): void => {
                    if (initial) {
                        // initial loading: get video feed and thumbnails as soon as possible
                        connectionRoomService
                            .getVideoFeedPromiseByStreamPipeIdNoList(scope.streamPipeId)
                            .then((videoFeed: Models.VideoFeed) => {
                                if (videoFeed.status !== Models.VideoFeedStatus.ERROR && !videoFeed.equals(scope.videofeed)) {
                                    scope.videofeed = videoFeed;
                                    scope.loadThumbnails();
                                }
                            });
                    } else {
                        connectionRoomService
                            .getVideoFeedPromiseByStreamPipeId(scope.streamPipeId)
                            .then((videoFeed: Models.VideoFeed) => {
                                if (videoFeed.status !== Models.VideoFeedStatus.ERROR && !videoFeed.equals(scope.videofeed)) {
                                    scope.videofeed = videoFeed;
                                }
                            });
                    }
                };

                if (scope.showThumb) {
                    this.initVideoFeed(scope, $interval);
                } else {
                    $timeout(() => {
                        scope.initStrobe();
                    });
                }
            };
        }

        private init(scope: IVideoPlayerLegacyScope): void {
            scope.playerId = Common.Utils.randomNumber(100000000);

            if (scope.streamUrl === undefined)
                scope.streamUrl = '';
            if (scope.type === undefined)
                scope.type = this.getType(scope.streamUrl);
            if (scope.autoplay === undefined)
                scope.autoplay = true;
            if (scope.loop === undefined)
                scope.loop = true;
            if (scope.live === undefined)
                scope.live = false;
            if (scope.videoScaling === undefined)
                scope.videoScaling = 'fill';
            if (scope.showThumb === undefined)
                scope.showThumb = false;
            scope.thumbEnabled = scope.showThumb;

            scope.streamType = this.getStreamType(scope.streamUrl);
        }

        private getType(streamUrl: string): string {
            streamUrl = streamUrl.toLowerCase();

            if (Common.Utils.endsWith(streamUrl, '.mp4')) {
                return "video/mp4";
            } else if (Common.Utils.startsWith(streamUrl, 'rtmp:') || Common.Utils.endsWith(streamUrl, '.flv')) {
                return "video/flv";
            } else if (Common.Utils.endsWith(streamUrl, '.mp3')) {
                return "audio/mpeg";
            } else if (Common.Utils.endsWith(streamUrl, '.m3u8')) {
                return "application/x-mpegURL";
            } else {
                return "video/mp4"; // default is mp4
            }
        }

        private getStreamType(streamUrl: string): string {
            streamUrl = streamUrl.toLowerCase();
            // only for rtmp streams
            return Common.Utils.startsWith(streamUrl, 'rtmp:') ? 'rtmp' : '';
        }

        private initVideoFeed(scope: IVideoPlayerLegacyScope, $interval: angular.IIntervalService): void {
            scope.loadVideoFeed(true);

            scope.videofeedTimer = $interval(() => {
                scope.loadVideoFeed();
            }, this.VIDEOFEED_TIMER_MS + Common.Utils.randomNumber(1000));

            scope.thumbnailTimer = $interval(() => {
                scope.loadThumbnails();
            }, this.THUMBNAIL_TIMER_MS + Common.Utils.randomNumber(1000));
        }
    }
}